import {
  aggregateConclusionCounts,
  BlockColors,
  COLOR_BLUE,
  COLOR_BROWN,
  COLOR_GREEN,
  COLOR_RED,
  COLOR_WHITE,
  COLOR_YELLOW,
  getLength,
  isThinning,
  isWeeding,
  mixColor,
  NormalizedBlock,
} from "./blocks";
import { BlockResponse } from "protos/portal/spatial";
import {
  carbonConvert,
  CycleSlot,
  getSpatialMetricName,
  MetricValidation,
  SpatialMetric,
  SpatialMetricBase,
  SpatialMetricBoolean,
  SpatialMetricDateTime,
  SpatialMetricDiscrete,
  SpatialMetricNumber,
} from "./metrics";
import { checkNever, isUndefined } from "./identity";
import { clamp, mean } from "./math";
import {
  ConclusionType,
  conclusionTypeFromJSON,
  WeedCounterChunk,
} from "protos/metrics/metrics";
import { convert, UnitNumberType } from "./units/units";
import { DateTime, Duration } from "luxon";
import { EntryTimingGrade } from "protos/portal/metrics";
import { findWhere } from "./arrays";
import { OpsCenterSpatialCropMetrics } from "protos/portal/jigsaw";
import { ParseKeys, TFunction } from "i18next";
import { SIZE_MEDIUM_TO_LARGE, SIZE_SMALL_TO_MEDIUM } from "./almanac";
import { values } from "./objects";

export const isSpatialOneOf = (id: string, list: SpatialMetric[]): boolean =>
  !isUndefined(findWhere(list, { id }));

const KILLED_CONCLUSIONS: ConclusionType[] = [
  conclusionTypeFromJSON(ConclusionType.SHOT),
];
const SKIPPED_CONCLUSIONS: ConclusionType[] = [
  conclusionTypeFromJSON(ConclusionType.INTERSECTS_WITH_NON_SHOOTABLE),
  conclusionTypeFromJSON(ConclusionType.OUT_OF_BAND),
  conclusionTypeFromJSON(ConclusionType.UNIMPORTANT),
  conclusionTypeFromJSON(ConclusionType.NOT_TARGETED),
];
const MISSED_CONCLUSIONS: ConclusionType[] = [
  conclusionTypeFromJSON(ConclusionType.ERROR),
  conclusionTypeFromJSON(ConclusionType.PARTIALLY_SHOT),
  conclusionTypeFromJSON(ConclusionType.NOT_SHOT),
  conclusionTypeFromJSON(ConclusionType.P2P_NOT_FOUND),
  conclusionTypeFromJSON(ConclusionType.P2P_MISSING_CONTEXT),
  conclusionTypeFromJSON(ConclusionType.MARKED_FOR_THINNING),
  conclusionTypeFromJSON(ConclusionType.OUT_OF_RANGE),
];

const COLORS_MORE_BAD: BlockColors = {
  colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
};

const COLORS_WEED_TYPE: BlockColors = {
  absoluteBreakpoints: [0, 25, 50],
  colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
  labels: [
    { i18nKey: "utils.descriptors.few" },
    { i18nKey: "utils.descriptors.minority" },
    { i18nKey: "utils.descriptors.majority" },
  ],
};

const COLORS_ACTIVITY: BlockColors = {
  absoluteBreakpoints: [0, 1],
  discrete: true,
  colors: [COLOR_RED, COLOR_GREEN],
};

const COLORS_MORE_GOOD: BlockColors = {
  colors: [COLOR_RED, COLOR_YELLOW, COLOR_GREEN],
};

const COLORS_LARGE_BAD: BlockColors = {
  colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
};

const COLORS_PERFORMANCE: BlockColors = {
  absoluteBreakpoints: [75, 90, 100],
  colors: [COLOR_RED, COLOR_YELLOW, COLOR_GREEN],
  labels: [
    { i18nKey: "utils.descriptors.poor" },
    { i18nKey: "utils.descriptors.ok" },
    { i18nKey: "utils.descriptors.good" },
  ],
};

const COLORS_SPEED: BlockColors = {
  absoluteBreakpoints: [0.2, 0.6, 1],
  colors: [COLOR_RED, COLOR_YELLOW, COLOR_GREEN],
  labels: [
    { i18nKey: "utils.descriptors.slow" },
    { i18nKey: "utils.descriptors.ok" },
    { i18nKey: "utils.descriptors.fast" },
  ],
};

const COLORS_SHOOT_TIME: BlockColors = {
  absoluteBreakpoints: [100, 300, 500],
  colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
  labels: [
    { i18nKey: "utils.descriptors.fast" },
    { i18nKey: "utils.descriptors.ok" },
    { i18nKey: "utils.descriptors.slow" },
  ],
};

const COLORS_CROP_DENSITY: BlockColors = {
  colors: [COLOR_RED, COLOR_GREEN, COLOR_BLUE],
};

const COLORS_KEPT_CROP_DENSITY: BlockColors = {
  colors: [COLOR_RED, COLOR_GREEN, COLOR_YELLOW],
};

export function getAvaliableSpatialMetricValue<ID extends string, T>(
  metric: SpatialMetricBase<ID, T>,
  block: NormalizedBlock
): T | undefined {
  return block.originalBlock.type === "jigsaw"
    ? metric.getJigsawValue(block.originalBlock.block)
    : metric.getValue(block.originalBlock.block);
}

const DEFAULT_METRIC: Pick<
  SpatialMetric,
  "canAverage" | "canTotal" | "canMap" | "validation" | "colors"
> = {
  canAverage: true,
  canTotal: true,
  canMap: true,
  validation: MetricValidation.PENDING,
  colors: {
    absoluteBreakpoints: [0, 100],
    colors: [COLOR_GREEN, COLOR_GREEN],
    labels: [
      { i18nKey: "utils.metrics.aggregates.min" },
      { i18nKey: "utils.metrics.aggregates.max" },
    ],
  },
};

const DEFAULT_NUMBER_METRIC: typeof DEFAULT_METRIC &
  Pick<SpatialMetricNumber, "getColor" | "type"> = {
  ...DEFAULT_METRIC,
  type: "number",
  getColor(value, range, isRelative = false) {
    const { colors } = this;
    colors.relativeBreakpoints = [range.low, range.medium, range.high];
    return mixColor(value, colors, isRelative);
  },
};

const DEFAULT_BOOLEAN_METRIC: typeof DEFAULT_METRIC &
  Pick<SpatialMetricBoolean, "getColor" | "type"> = {
  ...DEFAULT_METRIC,
  type: "boolean",
  getColor(value, range, isRelative = false) {
    const { colors } = this;
    colors.relativeBreakpoints = [range.low, range.medium, range.high];
    return mixColor(value ? 1 : 0, colors, isRelative);
  },
};

export const SPATIAL_AVERAGE_WEED_SIZE: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  units: "mm",
  id: "averageWeedSize",
  validation: MetricValidation.VALIDATED,
  colors: {
    ...COLORS_LARGE_BAD,
    absoluteBreakpoints: [
      0,
      mean([SIZE_SMALL_TO_MEDIUM, SIZE_MEDIUM_TO_LARGE]) ?? 0,
      SIZE_MEDIUM_TO_LARGE * 2,
    ],
    labels: [
      { i18nKey: "utils.descriptors.small" },
      { i18nKey: "utils.descriptors.medium" },
      { i18nKey: "utils.descriptors.large" },
    ],
  },
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(block.opsCenter.averageWeedRadiusMeters, "m", "mm"),
  getValue: (block) => {
    const cumulativeSize = block.block?.weedCount?.weedSizeData?.cumulativeSize;
    const totalWeeds = block.block?.weedCount?.weedSizeData?.count;
    if (!cumulativeSize || !totalWeeds) {
      return;
    }
    return cumulativeSize / totalWeeds;
  },
  cycleSlot: CycleSlot.SIZE,
};

export const SPATIAL_TOTAL_WEEDS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canMap: false,
  id: "totalWeeds",
  units: UnitNumberType.COUNT,
  validation: MetricValidation.INTERNAL,
  colors: COLORS_MORE_BAD,
  getJigsawValue: (block) => block.opsCenter?.weedsFound,
  getValue: (block) => block.block?.weedCount?.weedSizeData?.count,
};

export const SPATIAL_TOTAL_WEEDS_IN_BAND: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canMap: false,
  id: "totalWeedsInBand",
  units: UnitNumberType.COUNT,
  validation: MetricValidation.VALIDATED,
  colors: COLORS_MORE_BAD,
  getJigsawValue: (block) => block.opsCenter?.weedsInBand,
  getValue: (block) => {
    const killed = SPATIAL_KILLED_WEEDS.getValue(block);
    const skipped = SPATIAL_MISSED_WEEDS.getValue(block);
    return (killed ?? 0) + (skipped ?? 0);
  },
};

export const SPATIAL_AVERAGE_CROP_SIZE: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  units: "mm",
  id: "averageCropSize",
  validation: MetricValidation.VALIDATED,
  colors: COLORS_MORE_GOOD,
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(block.opsCenter.averageCropRadiusMeters, "m", "mm"),
  getValue: (block) => {
    const cumulativeSize = block.block?.weedCount?.cropSizeData?.cumulativeSize;
    const totalCrops = block.block?.weedCount?.cropSizeData?.count;
    if (!cumulativeSize || !totalCrops) {
      return;
    }
    return cumulativeSize / totalCrops;
  },
  cycleSlot: CycleSlot.SIZE,
};

export const SPATIAL_AVERAGE_TARGETED_REQ_LASER_TIME: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  units: "ms",
  id: "avgTargetedReqLaserTime",
  colors: COLORS_SHOOT_TIME,
  minY: 100,
  maxY: 300,
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(block.opsCenter.averageShootTimeSeconds, "s", "ms"),
  getValue: (block) => {
    if (!block.block?.weedCount?.targetedLaserTimeData) {
      return;
    }
    const cumulativeSize =
      block.block.weedCount.targetedLaserTimeData.cumulativeTime;
    const totalCount = block.block.weedCount.targetedLaserTimeData.count;
    if (!totalCount) {
      return;
    }
    return cumulativeSize / totalCount;
  },
  cycleSlot: CycleSlot.SHOOT,
};

// Each block is colored from its own average weed radius.
export const SPATIAL_ENTRY_TIMING: SpatialMetricDiscrete = {
  ...DEFAULT_METRIC,
  canAverage: false,
  canTotal: false,
  id: "entryTiming",
  type: "discrete",
  validation: MetricValidation.VALIDATED,
  colors: {
    absoluteBreakpoints: [
      EntryTimingGrade.ENTRY_TIMING_GRADE_IDEAL,
      EntryTimingGrade.ENTRY_TIMING_GRADE_ACCEPTABLE,
      EntryTimingGrade.ENTRY_TIMING_GRADE_LATE,
    ],
    colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
    discrete: true,
    labels: [
      {
        i18nKey: "components.EntryTimingGrade.ideal",
        captionI18nKey: "components.EntryTimingGrade.rangeIdeal",
      },
      {
        i18nKey: "components.EntryTimingGrade.acceptable",
        captionI18nKey: "components.EntryTimingGrade.rangeAcceptable",
      },
      {
        i18nKey: "components.EntryTimingGrade.late",
        captionI18nKey: "components.EntryTimingGrade.rangeLate",
      },
    ],
  },
  getColor(value) {
    if (value === undefined) {
      return COLOR_WHITE.toString();
    }
    return mixColor(value, this.colors, false);
  },
  getJigsawValue: (block) => block.entryTimingGrade,
  getValue: (block) => block.entryTimingGrade,
};

export const SPATIAL_TOTAL_VALID_CROPS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "totalCropsValid",
  units: UnitNumberType.COUNT,
  colors: COLORS_CROP_DENSITY,
  getJigsawValue: (block) => block.opsCenter?.cropsFound,
  getValue: (block) => block.block?.weedCount?.validCropCount,
};

export const SPATIAL_PERCENT_BANDED: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  canMap: false,
  id: "percentBanded",

  fill: true,
  units: UnitNumberType.PERCENT,
  minY: 0,
  maxY: 100,
  colors: {
    absoluteBreakpoints: [0, 50, 100],
    colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
  },
  getJigsawValue: (block) =>
    block.opsCenter?.bandedAreaFraction !== undefined
      ? block.opsCenter.bandedAreaFraction * 100
      : undefined,
  getValue: (block) =>
    clamp(0, 100, block.block?.bandingData?.percentBanded ?? 100),
};

const percentOfCategorized = (
  category: keyof WeedCounterChunk["countsByCategory"],
  block: BlockResponse
): number | undefined => {
  const totalCategory = block.block?.weedCount?.countsByCategory[category];
  const totalCategorized = values(
    block.block?.weedCount?.countsByCategory ?? {}
  ).reduce((partialSum, count) => partialSum + count, 0);
  if (totalCategory === undefined || !totalCategorized) {
    return;
  }
  return (totalCategory / totalCategorized) * 100;
};

export const SPATIAL_BROADLEAF: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "broadleaf",
  units: UnitNumberType.PERCENT,
  validation: MetricValidation.VALIDATED,
  colors: COLORS_WEED_TYPE,
  getJigsawValue: (block) =>
    block.opsCenter?.percentWeedsBroadleaf !== undefined
      ? block.opsCenter.percentWeedsBroadleaf * 100
      : undefined,
  getValue: (block) => percentOfCategorized("BROADLEAF", block),
};

export const SPATIAL_GRASS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "grass",
  units: UnitNumberType.PERCENT,
  validation: MetricValidation.VALIDATED,
  colors: COLORS_WEED_TYPE,
  getJigsawValue: (block) =>
    block.opsCenter?.percentWeedsGrass !== undefined
      ? block.opsCenter.percentWeedsGrass * 100
      : undefined,
  getValue: (block) => percentOfCategorized("GRASS", block),
};

export const SPATIAL_OFFSHOOT: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "offshoot",
  units: UnitNumberType.PERCENT,
  validation: MetricValidation.VALIDATED,
  colors: COLORS_WEED_TYPE,
  getJigsawValue: (block) =>
    block.opsCenter?.percentWeedsOffshoot !== undefined
      ? block.opsCenter.percentWeedsOffshoot * 100
      : undefined,
  getValue: (block) => percentOfCategorized("OFFSHOOT", block),
};

export const SPATIAL_PURSLANE: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "purslane",
  units: UnitNumberType.PERCENT,
  validation: MetricValidation.VALIDATED,
  colors: COLORS_WEED_TYPE,
  getJigsawValue: (block) =>
    block.opsCenter?.percentWeedsPurslane !== undefined
      ? block.opsCenter.percentWeedsPurslane * 100
      : undefined,
  getValue: (block) => percentOfCategorized("PURSLANE", block),
};

export const SPATIAL_COVERAGE: SpatialMetricBoolean = {
  ...DEFAULT_METRIC,
  canTotal: false,
  canAverage: false,
  id: "coverage",
  type: "boolean",
  validation: MetricValidation.VALIDATED,
  getColor: (isActive) =>
    isActive ? COLOR_BLUE.toString() : COLOR_WHITE.toString(),
  i18nKey: "active",
  getJigsawValue: (block) =>
    block.hardwareState?.wasThinning || block.hardwareState?.wasWeeding,
  getValue: (block) => isWeeding(block.block) || isThinning(block.block),
};

export const SPATIAL_TIME: SpatialMetricDateTime = {
  ...DEFAULT_METRIC,
  canTotal: false,
  canAverage: false,
  canMap: false,
  id: "dateTime",
  units: "ms",
  type: "dateTime",
  getColor: () => COLOR_WHITE.toString(),
  getJigsawValue: (block) =>
    block.start?.timestampMs
      ? DateTime.fromMillis(block.start.timestampMs).toJSDate()
      : undefined,
  getValue: (block) =>
    block.block?.start?.timestampMs
      ? DateTime.fromMillis(block.block.start.timestampMs).toJSDate()
      : undefined,
  cycleSlot: CycleSlot.TIME,
};

export const SPATIAL_ALTITUDE: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  canMap: false,
  id: "altitude",
  units: "ft",

  colors: {
    colors: [COLOR_BLUE, COLOR_GREEN, COLOR_BROWN],
  },
  getJigsawValue: (block) => {
    const altitudeMm = block.start?.heightMm;
    if (!altitudeMm) {
      return;
    }
    return convert(altitudeMm).from("mm").to("ft");
  },
  getValue: (block) => {
    const altitudeMm = block.block?.start?.heightMm;
    if (!altitudeMm) {
      return;
    }
    return convert(altitudeMm).from("mm").to("ft");
  },
  cycleSlot: CycleSlot.ALTITUDE,
};

export const SPATIAL_WEEDING: SpatialMetricBoolean = {
  ...DEFAULT_METRIC,
  canTotal: false,
  id: "weeding",
  type: "boolean",
  validation: MetricValidation.VALIDATED,
  i18nKey: "active",
  renderWhenNotCountable: true,
  colors: COLORS_ACTIVITY,
  getColor(value) {
    const falseColor = this.colors.colors[0];
    const trueColor = this.colors.colors[1];
    if (!falseColor || !trueColor) {
      return "";
    }
    return value ? trueColor.toString() : falseColor.toString();
  },
  getJigsawValue: (block) => block.hardwareState?.wasWeeding,
  getValue: (block) => isWeeding(block.block),
};

export const SPATIAL_THINNING: SpatialMetricBoolean = {
  ...DEFAULT_METRIC,
  canTotal: false,
  id: "thinning",
  type: "boolean",
  validation: MetricValidation.VALIDATED,
  i18nKey: "active",
  renderWhenNotCountable: true,
  colors: COLORS_ACTIVITY,
  getColor(value) {
    const falseColor = this.colors.colors[0];
    const trueColor = this.colors.colors[1];
    if (!falseColor || !trueColor) {
      return "";
    }
    return value ? trueColor.toString() : falseColor.toString();
  },
  getJigsawValue: (block) => block.hardwareState?.wasThinning,
  getValue: (block) => isThinning(block.block),
};

export const SPATIAL_KILLED_WEEDS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "weedsKilled",
  units: UnitNumberType.COUNT,
  colors: COLORS_MORE_GOOD,
  getJigsawValue: (block) => block.opsCenter?.weedsKilled,
  getValue: (block) =>
    block.block?.weedCount?.conclusionCounts?.armedWeed[
      conclusionTypeFromJSON(ConclusionType.SHOT)
    ],
};

export const SPATIAL_SKIPPED_WEEDS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "weedsSkipped",
  units: UnitNumberType.COUNT,
  colors: COLORS_MORE_BAD,
  getJigsawValue: (block) => block.opsCenter?.weedsIgnored,
  getValue: (block) =>
    aggregateConclusionCounts(
      block.block?.weedCount?.conclusionCounts?.armedWeed ?? [],
      SKIPPED_CONCLUSIONS
    ),
};

export const SPATIAL_MISSED_WEEDS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "weedsMissed",
  units: UnitNumberType.COUNT,
  colors: COLORS_MORE_BAD,
  getJigsawValue: (block) => block.opsCenter?.weedsMissed,
  getValue: (block) =>
    aggregateConclusionCounts(
      block.block?.weedCount?.conclusionCounts?.armedWeed ?? [],
      MISSED_CONCLUSIONS
    ),
};

export const SPATIAL_KILLED_CROPS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "cropsKilled",
  units: UnitNumberType.COUNT,
  colors: COLORS_MORE_GOOD,
  getJigsawValue: (block) => block.opsCenter?.cropsThinned,
  getValue: (block) =>
    aggregateConclusionCounts(
      block.block?.weedCount?.conclusionCounts?.armedCrop ?? [],
      KILLED_CONCLUSIONS
    ),
};

export const SPATIAL_SKIPPED_CROPS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "cropsSkipped",
  units: UnitNumberType.COUNT,
  colors: COLORS_MORE_BAD,
  getJigsawValue: (block) => block.opsCenter?.cropsIgnored,
  getValue: (block) =>
    aggregateConclusionCounts(
      block.block?.weedCount?.conclusionCounts?.armedCrop ?? [],
      SKIPPED_CONCLUSIONS
    ),
};

export const SPATIAL_MISSED_CROPS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "cropsMissed",
  units: UnitNumberType.COUNT,
  colors: COLORS_MORE_BAD,
  getJigsawValue: (block) => block.opsCenter?.cropsMissed,
  getValue: (block) =>
    aggregateConclusionCounts(
      block.block?.weedCount?.conclusionCounts?.armedCrop ?? [],
      MISSED_CONCLUSIONS
    ),
};

export const SPATIAL_KEPT_CROPS: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  id: "cropsKept",
  units: UnitNumberType.COUNT,
  colors: COLORS_KEPT_CROP_DENSITY,
  getJigsawValue: (block) => block.opsCenter?.cropsKept,
  getValue: (block) => {
    const total = block.block?.weedCount?.cropSizeData?.count;
    const killed = SPATIAL_KILLED_CROPS.getValue(block);
    return total === undefined || killed === undefined
      ? undefined
      : total - killed;
  },
};

export const SPATIAL_AVERAGE_SPEED: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "speedTravel",
  units: "mph",
  validation: MetricValidation.VALIDATED,
  minY: 0,
  maxY: 2,
  colors: COLORS_SPEED,
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(
          block.opsCenter.averageTravelSpeedMetersPerSecond,
          "m/s",
          "mph"
        ),
  getValue: (block) => {
    const startTime = block.block?.start?.timestampMs;
    const endTime = block.block?.end?.timestampMs;
    const length = getLength(block, "miles");
    if (!startTime || !endTime || isUndefined(length)) {
      return;
    }
    const time = Duration.fromMillis(endTime - startTime);
    return length / time.as("hours");
  },
  cycleSlot: CycleSlot.SPEED,
};

export const SPATIAL_SMOOTHED_TARGET_SPEED: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "speedTargetSmoothed",
  units: "mph",
  validation: MetricValidation.VALIDATED,
  minY: 0,
  maxY: 2,
  colors: COLORS_SPEED,
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(
          block.opsCenter.averageTargetSpeedMetersPerSecond,
          "m/s",
          "mph"
        ),
  getValue: (block) => {
    const smoothedTargetSpeed = block.block?.velData?.avgTargetVel.cmd;
    if (!smoothedTargetSpeed) {
      return;
    }
    return convert(smoothedTargetSpeed).from("m/s").to("mph");
  },
  cycleSlot: CycleSlot.SPEED,
};

export const SPATIAL_SPEED_EFFICIENCY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "speed",
  units: UnitNumberType.PERCENT,
  minY: 0,
  maxY: 100,
  validation: MetricValidation.VALIDATED,
  colors: {
    absoluteBreakpoints: [75, 100, 110, 125],
    colors: [COLOR_BLUE, COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
    labels: [
      { i18nKey: "utils.descriptors.slow" },
      { i18nKey: "utils.descriptors.good" },
      { i18nKey: "utils.descriptors.ok" },
      { i18nKey: "utils.descriptors.fast" },
    ],
  },
  getJigsawValue: (block) =>
    block.opsCenter?.targetSpeedRatio !== undefined
      ? block.opsCenter.targetSpeedRatio * 100
      : undefined,
  getValue: (block) => {
    const speed = SPATIAL_AVERAGE_SPEED.getValue(block);
    const target = SPATIAL_SMOOTHED_TARGET_SPEED.getValue(block);
    if (!speed || !target) {
      return;
    }
    return (speed / target) * 100;
  },
};

export const SPATIAL_WEED_DENSITY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "weedDensity",
  units: "/ft2",
  validation: MetricValidation.VALIDATED,
  colors: {
    absoluteBreakpoints: [0, 15, 25],
    colors: [COLOR_GREEN, COLOR_YELLOW, COLOR_RED],
    labels: [
      { i18nKey: "utils.descriptors.sparse" },
      { i18nKey: "utils.descriptors.ok" },
      { i18nKey: "utils.descriptors.dense" },
    ],
  },
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(block.opsCenter.weedDensityPerSqMeter, "/m2", "/ft2"),
  getValue: (block) => {
    const widthMm =
      block.block?.implementWidthData?.widthMm ??
      convert(20).from("ft").to("mm");
    const length = getLength(block, "feet");
    if (!widthMm || isUndefined(length)) {
      return;
    }
    const totalWeeds = block.block?.weedCount?.weedSizeData?.count;
    const area = convert(widthMm).from("mm").to("ft") * length;
    if (totalWeeds === undefined || !area) {
      return;
    }
    return totalWeeds / area;
  },
  cycleSlot: CycleSlot.DENSITY,
};

export const SPATIAL_CROP_DENSITY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "cropDensity",
  units: "/ft2",
  validation: MetricValidation.VALIDATED,
  colors: COLORS_CROP_DENSITY,
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(block.opsCenter.cropDensityPerSqMeter, "/m2", "/ft2"),
  getValue: (block) => {
    const widthMm = block.block?.implementWidthData?.widthMm;
    const length = getLength(block, "feet");
    if (!widthMm || isUndefined(length)) {
      return;
    }
    const totalCrops = block.block?.weedCount?.cropSizeData?.count;
    const area = convert(widthMm).from("mm").to("ft") * length;
    if (totalCrops === undefined || !area) {
      return;
    }
    return totalCrops / area;
  },
  cycleSlot: CycleSlot.DENSITY,
};

export const SPATIAL_REMAINING_CROP_DENSITY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: "remainingCropDensity",
  units: "/ft2",
  validation: MetricValidation.VALIDATED,
  colors: COLORS_KEPT_CROP_DENSITY,
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : carbonConvert(
          block.opsCenter.remainingCropDensityPerSqMeter,
          "/m2",
          "/ft2"
        ),
  getValue: (block) => {
    const widthMm = block.block?.implementWidthData?.widthMm;
    const length = getLength(block, "feet");
    if (!widthMm || isUndefined(length)) {
      return;
    }
    const totalCrops = block.block?.weedCount?.cropSizeData?.count;
    const killedCrops = SPATIAL_KILLED_CROPS.getValue(block);
    const area = convert(widthMm).from("mm").to("ft") * length;
    if (totalCrops === undefined || killedCrops === undefined || !area) {
      return;
    }
    return (totalCrops - killedCrops) / area;
  },
  cycleSlot: CycleSlot.DENSITY,
};

const getShotPercent = (conclusionCounts: number[][]): number | undefined => {
  let shot = 0;
  let missed = 0;
  for (const counts of conclusionCounts) {
    shot += aggregateConclusionCounts(counts, KILLED_CONCLUSIONS);
    missed += aggregateConclusionCounts(counts, MISSED_CONCLUSIONS);
  }
  if (shot + missed === 0) {
    return;
  }
  return (shot / (shot + missed)) * 100;
};

export const SPATIAL_WEEDING_EFFICIENCY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  units: UnitNumberType.PERCENT,
  minY: 0,
  maxY: 100,
  canTotal: false,
  id: "weedingEfficiency",
  validation: MetricValidation.VALIDATED,
  colors: COLORS_PERFORMANCE,
  getJigsawValue: (block) =>
    block.opsCenter?.weedingPerformance !== undefined
      ? block.opsCenter.weedingPerformance * 100
      : undefined,
  getValue: (block) =>
    getShotPercent([block.block?.weedCount?.conclusionCounts?.armedWeed ?? []]),
};

export const SPATIAL_OVERALL_EFFICIENCY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  units: UnitNumberType.PERCENT,
  minY: 0,
  maxY: 100,
  canTotal: false,
  id: "overallEfficiency",
  validation: MetricValidation.VALIDATED,
  colors: COLORS_PERFORMANCE,
  getJigsawValue: (block) =>
    block.opsCenter?.overallPerformance !== undefined
      ? block.opsCenter.overallPerformance * 100
      : undefined,
  getValue: (block) =>
    getShotPercent([
      block.block?.weedCount?.conclusionCounts?.armedWeed ?? [],
      block.block?.weedCount?.conclusionCounts?.armedCrop ?? [],
    ]),
};

export const SPATIAL_THINNING_EFFICIENCY: SpatialMetricNumber = {
  ...DEFAULT_NUMBER_METRIC,
  units: UnitNumberType.PERCENT,
  minY: 0,
  maxY: 100,
  canTotal: false,
  id: "thinningEfficiency",
  validation: MetricValidation.VALIDATED,
  colors: COLORS_PERFORMANCE,
  getJigsawValue: (block) =>
    block.opsCenter?.thinningPerformance !== undefined
      ? block.opsCenter.thinningPerformance * 100
      : undefined,
  getValue: (block) =>
    getShotPercent([block.block?.weedCount?.conclusionCounts?.armedCrop ?? []]),
};

export const SPATIAL_LIFTED: SpatialMetricBoolean = {
  ...DEFAULT_BOOLEAN_METRIC,
  canTotal: false,
  canAverage: false,
  id: "lifted",
  validation: MetricValidation.INTERNAL,
  i18nKey: "liftedOn",
  renderWhenNotCountable: true,
  colors: {
    absoluteBreakpoints: [0, 1],
    discrete: true,
    colors: [COLOR_GREEN, COLOR_RED],
    labels: [
      { i18nKey: "utils.descriptors.liftedOff" },
      { i18nKey: "utils.descriptors.liftedOn" },
    ],
  },
  getJigsawValue: (block) => block.hardwareState?.lifted,
  getValue: (block) => block.block?.hwMetric?.lifted ?? false,
};

export const SPATIAL_ESTOPPED: SpatialMetricBoolean = {
  ...DEFAULT_BOOLEAN_METRIC,
  canTotal: false,
  canAverage: false,
  id: "estopped",
  validation: MetricValidation.INTERNAL,
  i18nKey: "estopOn",
  renderWhenNotCountable: true,
  colors: {
    absoluteBreakpoints: [0, 1],
    discrete: true,
    colors: [COLOR_GREEN, COLOR_RED],
    labels: [
      { i18nKey: "utils.descriptors.estopOff" },
      { i18nKey: "utils.descriptors.estopOn" },
    ],
  },
  getJigsawValue: (block) => block.hardwareState?.eStop,
  getValue: (block) => block.block?.hwMetric?.estopped ?? false,
};

export const SPATIAL_LASER_KEY: SpatialMetricBoolean = {
  ...DEFAULT_BOOLEAN_METRIC,
  canTotal: false,
  canAverage: false,
  id: "laserKey",
  validation: MetricValidation.INTERNAL,
  i18nKey: "laserKeyOn",
  renderWhenNotCountable: true,
  colors: {
    absoluteBreakpoints: [0, 1],
    discrete: true,
    colors: [COLOR_RED, COLOR_GREEN],
    labels: [
      { i18nKey: "utils.descriptors.laserKeyOff" },
      { i18nKey: "utils.descriptors.laserKeyOn" },
    ],
  },
  getJigsawValue: (block) => block.hardwareState?.laserKey,
  getValue: (block) => block.block?.hwMetric?.laserKey,
};

export const SPATIAL_INTERLOCK: SpatialMetricBoolean = {
  ...DEFAULT_BOOLEAN_METRIC,
  canTotal: false,
  canAverage: false,
  id: "interlock",
  validation: MetricValidation.INTERNAL,
  i18nKey: "interlockSafe",
  renderWhenNotCountable: true,
  colors: {
    absoluteBreakpoints: [0, 1],
    discrete: true,
    colors: [COLOR_RED, COLOR_GREEN],
    labels: [
      { i18nKey: "utils.descriptors.interlockUnsafe" },
      { i18nKey: "utils.descriptors.interlockSafe" },
    ],
  },
  getJigsawValue: (block) => block.hardwareState?.interlock,
  getValue: (block) => block.block?.hwMetric?.interlock,
};

export const SPATIAL_WATER_PROTECT: SpatialMetricBoolean = {
  ...DEFAULT_BOOLEAN_METRIC,
  canTotal: false,
  canAverage: false,
  id: "waterProtect",
  validation: MetricValidation.INTERNAL,
  i18nKey: "waterProtectNormal",
  renderWhenNotCountable: true,
  colors: {
    absoluteBreakpoints: [0, 1],
    discrete: true,
    colors: [COLOR_RED, COLOR_GREEN],
    labels: [
      { i18nKey: "utils.descriptors.waterProtectTriggered" },
      { i18nKey: "utils.descriptors.waterProtectNormal" },
    ],
  },
  getJigsawValue: (block) => block.hardwareState?.waterProtection,
  getValue: (block) => block.block?.hwMetric?.waterProtect,
};

// add popups (have to do this after all of them are declared)
SPATIAL_COVERAGE.popup = [SPATIAL_WEEDING, SPATIAL_THINNING];
SPATIAL_TIME.popup = [];
SPATIAL_ALTITUDE.popup = [SPATIAL_ALTITUDE];
SPATIAL_PERCENT_BANDED.popup = [SPATIAL_PERCENT_BANDED];
SPATIAL_AVERAGE_TARGETED_REQ_LASER_TIME.popup = [
  SPATIAL_AVERAGE_TARGETED_REQ_LASER_TIME,
];
SPATIAL_AVERAGE_SPEED.popup = [
  SPATIAL_AVERAGE_SPEED,
  SPATIAL_SMOOTHED_TARGET_SPEED,
  SPATIAL_SPEED_EFFICIENCY,
];
SPATIAL_SMOOTHED_TARGET_SPEED.popup = [
  SPATIAL_SMOOTHED_TARGET_SPEED,
  SPATIAL_AVERAGE_SPEED,
  SPATIAL_SPEED_EFFICIENCY,
];
SPATIAL_SPEED_EFFICIENCY.popup = [
  SPATIAL_SPEED_EFFICIENCY,
  SPATIAL_AVERAGE_SPEED,
  SPATIAL_SMOOTHED_TARGET_SPEED,
];
SPATIAL_WEEDING.popup = [
  SPATIAL_WEEDING,
  SPATIAL_THINNING,
  SPATIAL_PERCENT_BANDED,
];
SPATIAL_WEEDING_EFFICIENCY.popup = [
  SPATIAL_WEEDING_EFFICIENCY,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_KILLED_WEEDS,
  SPATIAL_SKIPPED_WEEDS,
  SPATIAL_MISSED_WEEDS,
];
SPATIAL_THINNING.popup = [
  SPATIAL_THINNING,
  SPATIAL_WEEDING,
  SPATIAL_PERCENT_BANDED,
];
SPATIAL_OVERALL_EFFICIENCY.popup = [
  SPATIAL_OVERALL_EFFICIENCY,
  SPATIAL_WEEDING_EFFICIENCY,
  SPATIAL_THINNING_EFFICIENCY,
];
SPATIAL_THINNING_EFFICIENCY.popup = [
  SPATIAL_THINNING_EFFICIENCY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KEPT_CROPS,
  SPATIAL_KILLED_CROPS,
  SPATIAL_SKIPPED_CROPS,
  SPATIAL_MISSED_CROPS,
];
SPATIAL_AVERAGE_WEED_SIZE.popup = [
  SPATIAL_AVERAGE_WEED_SIZE,
  SPATIAL_WEED_DENSITY,
  SPATIAL_TOTAL_WEEDS,
];
SPATIAL_ENTRY_TIMING.popup = [SPATIAL_ENTRY_TIMING, SPATIAL_AVERAGE_WEED_SIZE];
SPATIAL_AVERAGE_CROP_SIZE.popup = [
  SPATIAL_AVERAGE_CROP_SIZE,
  SPATIAL_CROP_DENSITY,
  SPATIAL_TOTAL_VALID_CROPS,
];
SPATIAL_WEED_DENSITY.popup = [
  SPATIAL_WEED_DENSITY,
  SPATIAL_AVERAGE_WEED_SIZE,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_TOTAL_WEEDS_IN_BAND,
];
SPATIAL_TOTAL_WEEDS.popup = [
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_TOTAL_WEEDS_IN_BAND,
  SPATIAL_WEED_DENSITY,
  SPATIAL_WEEDING_EFFICIENCY,
];
SPATIAL_TOTAL_WEEDS_IN_BAND.popup = [
  SPATIAL_TOTAL_WEEDS_IN_BAND,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_WEED_DENSITY,
  SPATIAL_WEEDING_EFFICIENCY,
];
SPATIAL_KILLED_WEEDS.popup = [
  SPATIAL_KILLED_WEEDS,
  SPATIAL_WEEDING_EFFICIENCY,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_TOTAL_WEEDS_IN_BAND,
  SPATIAL_SKIPPED_WEEDS,
  SPATIAL_MISSED_WEEDS,
];
SPATIAL_MISSED_WEEDS.popup = [
  SPATIAL_MISSED_WEEDS,
  SPATIAL_WEEDING_EFFICIENCY,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_TOTAL_WEEDS_IN_BAND,
  SPATIAL_KILLED_WEEDS,
  SPATIAL_SKIPPED_WEEDS,
];
SPATIAL_SKIPPED_WEEDS.popup = [
  SPATIAL_SKIPPED_WEEDS,
  SPATIAL_WEEDING_EFFICIENCY,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_TOTAL_WEEDS_IN_BAND,
  SPATIAL_KILLED_WEEDS,
  SPATIAL_MISSED_WEEDS,
];
SPATIAL_BROADLEAF.popup = [
  SPATIAL_BROADLEAF,
  SPATIAL_GRASS,
  SPATIAL_OFFSHOOT,
  SPATIAL_PURSLANE,
  SPATIAL_TOTAL_WEEDS,
];
SPATIAL_GRASS.popup = [
  SPATIAL_GRASS,
  SPATIAL_BROADLEAF,
  SPATIAL_OFFSHOOT,
  SPATIAL_PURSLANE,
  SPATIAL_TOTAL_WEEDS,
];
SPATIAL_OFFSHOOT.popup = [
  SPATIAL_OFFSHOOT,
  SPATIAL_BROADLEAF,
  SPATIAL_GRASS,
  SPATIAL_PURSLANE,
  SPATIAL_TOTAL_WEEDS,
];
SPATIAL_PURSLANE.popup = [
  SPATIAL_PURSLANE,
  SPATIAL_BROADLEAF,
  SPATIAL_GRASS,
  SPATIAL_OFFSHOOT,
  SPATIAL_TOTAL_WEEDS,
];
SPATIAL_AVERAGE_CROP_SIZE.popup = [
  SPATIAL_AVERAGE_CROP_SIZE,
  SPATIAL_CROP_DENSITY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KEPT_CROPS,
];
SPATIAL_CROP_DENSITY.popup = [
  SPATIAL_CROP_DENSITY,
  SPATIAL_REMAINING_CROP_DENSITY,
  SPATIAL_AVERAGE_CROP_SIZE,
  SPATIAL_TOTAL_VALID_CROPS,
];
SPATIAL_REMAINING_CROP_DENSITY.popup = [
  SPATIAL_REMAINING_CROP_DENSITY,
  SPATIAL_CROP_DENSITY,
  SPATIAL_AVERAGE_CROP_SIZE,
  SPATIAL_KEPT_CROPS,
  SPATIAL_MISSED_CROPS,
  SPATIAL_TOTAL_VALID_CROPS,
];
SPATIAL_KILLED_CROPS.popup = [
  SPATIAL_KILLED_CROPS,
  SPATIAL_THINNING_EFFICIENCY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KEPT_CROPS,
  SPATIAL_SKIPPED_CROPS,
  SPATIAL_MISSED_CROPS,
];
SPATIAL_MISSED_CROPS.popup = [
  SPATIAL_MISSED_CROPS,
  SPATIAL_THINNING_EFFICIENCY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KEPT_CROPS,
  SPATIAL_KILLED_CROPS,
  SPATIAL_SKIPPED_CROPS,
];
SPATIAL_SKIPPED_CROPS.popup = [
  SPATIAL_SKIPPED_CROPS,
  SPATIAL_THINNING_EFFICIENCY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KEPT_CROPS,
  SPATIAL_KILLED_CROPS,
  SPATIAL_MISSED_CROPS,
];
SPATIAL_KEPT_CROPS.popup = [
  SPATIAL_KEPT_CROPS,
  SPATIAL_REMAINING_CROP_DENSITY,
  SPATIAL_THINNING_EFFICIENCY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KILLED_CROPS,
  SPATIAL_SKIPPED_CROPS,
  SPATIAL_MISSED_CROPS,
];
SPATIAL_LIFTED.popup = [
  SPATIAL_LIFTED,
  SPATIAL_ESTOPPED,
  SPATIAL_LASER_KEY,
  SPATIAL_INTERLOCK,
  SPATIAL_WATER_PROTECT,
];
SPATIAL_ESTOPPED.popup = [
  SPATIAL_ESTOPPED,
  SPATIAL_LIFTED,
  SPATIAL_LASER_KEY,
  SPATIAL_INTERLOCK,
  SPATIAL_WATER_PROTECT,
];
SPATIAL_LASER_KEY.popup = [
  SPATIAL_LASER_KEY,
  SPATIAL_LIFTED,
  SPATIAL_ESTOPPED,
  SPATIAL_INTERLOCK,
  SPATIAL_WATER_PROTECT,
];
SPATIAL_INTERLOCK.popup = [
  SPATIAL_INTERLOCK,
  SPATIAL_LIFTED,
  SPATIAL_ESTOPPED,
  SPATIAL_LASER_KEY,
  SPATIAL_WATER_PROTECT,
];
SPATIAL_WATER_PROTECT.popup = [
  SPATIAL_WATER_PROTECT,
  SPATIAL_LIFTED,
  SPATIAL_ESTOPPED,
  SPATIAL_LASER_KEY,
  SPATIAL_INTERLOCK,
];

// === Per-category jigsaw metrics ===========================================
// Jigsaw ops-center blocks carry weed/crop metrics per plant category
// (weedMetrics / cropMetrics, keyed by bare category id). Map layers for them
// are created on the fly from a SpatialMetricSelection. These metrics are
// only produced by the jigsaw-gated map UI, so they are never resolved
// against legacy (non-jigsaw) blocks and getValue returns undefined.

export const CROP_CATEGORY_BASE_METRIC_KEYS = [
  "density",
  "averageRadius",
  "found",
] as const;
export const CROP_CATEGORY_THINNING_METRIC_KEYS = [
  "remainingDensity",
  "kept",
  "thinned",
  "missed",
  "ignored",
] as const;
export type CropCategoryMetricKey =
  | (typeof CROP_CATEGORY_BASE_METRIC_KEYS)[number]
  | (typeof CROP_CATEGORY_THINNING_METRIC_KEYS)[number];

// The map's heatmap layer choice: a static metric by id, or a per-category
// layer described by its parts.
export type SpatialMetricSelection =
  | { kind: "static"; id: string }
  | { kind: "weedCategory"; categoryId: string }
  | { kind: "cropCategory"; categoryId: string; key: CropCategoryMetricKey };

export const resolveSpatialMetric = (
  selection: SpatialMetricSelection
): SpatialMetric | undefined => {
  switch (selection.kind) {
    case "static": {
      return findWhere(SPATIAL_METRICS, { id: selection.id });
    }
    case "weedCategory": {
      return makeWeedCategoryMetric(selection.categoryId);
    }
    case "cropCategory": {
      return makeCropCategoryMetric(selection.categoryId, selection.key);
    }
    default: {
      checkNever(selection);
    }
  }
};

// Unique `SpatialMetric.id` strings for the dynamic metrics (used as React
// keys and display-label lookups, never decoded).
const weedCategoryMetricId = (categoryId: string): string =>
  `weedCategory:${categoryId}`;

const cropCategoryMetricId = (
  categoryId: string,
  key: CropCategoryMetricKey
): string => `cropCategory:${categoryId}:${key}`;

const noLegacyValue = (): undefined => {
  // per-category metrics only exist on jigsaw blocks; legacy blocks have no
  // per-category data
};

export const makeWeedCategoryMetric = (
  categoryId: string,
  displayName?: string
): SpatialMetricNumber => ({
  ...DEFAULT_NUMBER_METRIC,
  canTotal: false,
  id: weedCategoryMetricId(categoryId) as SpatialMetricNumber["id"],
  categoryId,
  categoryKind: "weed",
  displayName,
  units: UnitNumberType.PERCENT,
  validation: MetricValidation.VALIDATED,
  colors: COLORS_WEED_TYPE,
  // categories absent from a block's map contributed no weeds there: 0%, not
  // "no data"
  getJigsawValue: (block) =>
    block.opsCenter === undefined
      ? undefined
      : (block.opsCenter.weedMetrics[categoryId]?.fractionOfTotal ?? 0) * 100,
  getValue: noLegacyValue,
});

const CROP_CATEGORY_METRIC_SPECS: Record<
  CropCategoryMetricKey,
  {
    base: SpatialMetricNumber;
    getCategoryValue: (
      entry: OpsCenterSpatialCropMetrics | undefined
    ) => number | undefined;
  }
> = {
  density: {
    base: SPATIAL_CROP_DENSITY,
    getCategoryValue: (entry) =>
      carbonConvert(entry?.densityPerSquareMeter ?? 0, "/m2", "/ft2"),
  },
  averageRadius: {
    base: SPATIAL_AVERAGE_CROP_SIZE,
    getCategoryValue: (entry) =>
      entry === undefined || entry.found <= 0
        ? undefined
        : carbonConvert(entry.averageRadiusMeters, "m", "mm"),
  },
  found: {
    base: SPATIAL_TOTAL_VALID_CROPS,
    getCategoryValue: (entry) => entry?.found ?? 0,
  },
  remainingDensity: {
    base: SPATIAL_REMAINING_CROP_DENSITY,
    getCategoryValue: (entry) =>
      carbonConvert(entry?.remainingCropDensityPerSqMeter ?? 0, "/m2", "/ft2"),
  },
  kept: {
    base: SPATIAL_KEPT_CROPS,
    getCategoryValue: (entry) => entry?.kept ?? 0,
  },
  thinned: {
    base: SPATIAL_KILLED_CROPS,
    getCategoryValue: (entry) => entry?.thinned ?? 0,
  },
  missed: {
    base: SPATIAL_MISSED_CROPS,
    getCategoryValue: (entry) => entry?.missed ?? 0,
  },
  ignored: {
    base: SPATIAL_SKIPPED_CROPS,
    getCategoryValue: (entry) => entry?.ignored ?? 0,
  },
};

// Bare name of a crop category metric, for UI that labels the metric without
// naming the category.
export const getCropCategoryMetricName = (
  t: TFunction,
  key: CropCategoryMetricKey
): string => getSpatialMetricName(t, CROP_CATEGORY_METRIC_SPECS[key].base);

export const makeCropCategoryMetric = (
  categoryId: string,
  key: CropCategoryMetricKey
): SpatialMetricNumber => {
  const spec = CROP_CATEGORY_METRIC_SPECS[key];
  // drop the base popup: the per-category popup is assembled by the map,
  // which knows the full category list
  const { popup: _popup, ...base } = spec.base;
  return {
    ...base,
    id: cropCategoryMetricId(categoryId, key) as SpatialMetricNumber["id"],
    // labeled by the base metric's translation key, not the dynamic id
    i18nId: spec.base.i18nId ?? spec.base.id,
    categoryId,
    categoryKind: "crop",
    getJigsawValue: (block) =>
      block.opsCenter === undefined
        ? undefined
        : spec.getCategoryValue(block.opsCenter.cropMetrics[categoryId]),
    getValue: noLegacyValue,
  };
};

export const SPATIAL_WEEDING_METRICS: SpatialMetric[] = [
  SPATIAL_KILLED_WEEDS,
  SPATIAL_MISSED_WEEDS,
  SPATIAL_SKIPPED_WEEDS,
];

export const SPATIAL_THINNING_METRICS: SpatialMetric[] = [
  SPATIAL_KEPT_CROPS,
  SPATIAL_REMAINING_CROP_DENSITY,
  SPATIAL_KILLED_CROPS,
  SPATIAL_MISSED_CROPS,
  SPATIAL_SKIPPED_CROPS,
];

export const SPATIAL_DEFAULT_SELECTION: SpatialMetricSelection = {
  kind: "static",
  id: SPATIAL_AVERAGE_SPEED.id,
};

export const isDefaultSpatialSelection = (
  selection: SpatialMetricSelection
): boolean =>
  selection.kind === "static" && selection.id === SPATIAL_DEFAULT_SELECTION.id;

export const SPATIAL_METRICS = [
  SPATIAL_COVERAGE,

  // field
  SPATIAL_TIME,
  SPATIAL_ALTITUDE,
  SPATIAL_PERCENT_BANDED,
  SPATIAL_AVERAGE_SPEED,
  SPATIAL_SMOOTHED_TARGET_SPEED,
  SPATIAL_SPEED_EFFICIENCY,
  SPATIAL_WEEDING,
  SPATIAL_WEEDING_EFFICIENCY,
  SPATIAL_THINNING,
  SPATIAL_THINNING_EFFICIENCY,
  SPATIAL_OVERALL_EFFICIENCY,
  SPATIAL_AVERAGE_TARGETED_REQ_LASER_TIME,

  // weeds
  SPATIAL_AVERAGE_WEED_SIZE,
  SPATIAL_ENTRY_TIMING,
  SPATIAL_WEED_DENSITY,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_TOTAL_WEEDS_IN_BAND,
  SPATIAL_KILLED_WEEDS,
  SPATIAL_MISSED_WEEDS,
  SPATIAL_SKIPPED_WEEDS,
  SPATIAL_BROADLEAF,
  SPATIAL_GRASS,
  SPATIAL_OFFSHOOT,
  SPATIAL_PURSLANE,

  // crops
  SPATIAL_AVERAGE_CROP_SIZE,
  SPATIAL_CROP_DENSITY,
  SPATIAL_REMAINING_CROP_DENSITY,
  SPATIAL_TOTAL_VALID_CROPS,
  SPATIAL_KEPT_CROPS,
  SPATIAL_KILLED_CROPS,
  SPATIAL_MISSED_CROPS,
  SPATIAL_SKIPPED_CROPS,

  // hardware
  SPATIAL_LIFTED,
  SPATIAL_ESTOPPED,
  SPATIAL_LASER_KEY,
  SPATIAL_INTERLOCK,
  SPATIAL_WATER_PROTECT,
];

export type SpatialMetricGroupKey =
  | "speed"
  | "performance"
  | "weeds"
  | "crops"
  | "hardware"
  | "speedDetails";

export interface SpatialMetricGroup {
  key: SpatialMetricGroupKey;
  i18nKey: ParseKeys;
  // If true, this entire group will be hidden from customers.
  internal?: boolean;
  metrics: SpatialMetric[];
}

// The fixed-category weed-type layers, replaced by per-category weed metrics
// when jigsaw data is shown.
export const SPATIAL_STATIC_WEED_TYPE_METRICS: SpatialMetric[] = [
  SPATIAL_BROADLEAF,
  SPATIAL_GRASS,
  SPATIAL_OFFSHOOT,
  SPATIAL_PURSLANE,
];

export const SPATIAL_METRIC_GROUPS: SpatialMetricGroup[] = [
  {
    key: "speed",
    i18nKey: "utils.metrics.groups.speed",
    metrics: [
      SPATIAL_AVERAGE_SPEED,
      SPATIAL_SMOOTHED_TARGET_SPEED,
      SPATIAL_PERCENT_BANDED,
      SPATIAL_SPEED_EFFICIENCY,
    ],
  },
  {
    key: "performance",
    i18nKey: "utils.metrics.groups.performance",
    metrics: [
      SPATIAL_OVERALL_EFFICIENCY,
      SPATIAL_WEEDING_EFFICIENCY,
      SPATIAL_WEEDING,
      SPATIAL_THINNING_EFFICIENCY,
      SPATIAL_THINNING,
      SPATIAL_AVERAGE_TARGETED_REQ_LASER_TIME,
    ],
  },
  {
    key: "weeds",
    i18nKey: "models.weeds.weed_other",
    metrics: [
      SPATIAL_WEED_DENSITY,
      SPATIAL_AVERAGE_WEED_SIZE,
      SPATIAL_ENTRY_TIMING,
      SPATIAL_TOTAL_WEEDS,
      SPATIAL_KILLED_WEEDS,
      SPATIAL_MISSED_WEEDS,
      SPATIAL_SKIPPED_WEEDS,
      SPATIAL_BROADLEAF,
      SPATIAL_GRASS,
      SPATIAL_OFFSHOOT,
      SPATIAL_PURSLANE,
    ],
  },
  {
    key: "crops",
    i18nKey: "models.crops.crop_other",
    metrics: [
      SPATIAL_CROP_DENSITY,
      SPATIAL_REMAINING_CROP_DENSITY,
      SPATIAL_AVERAGE_CROP_SIZE,
      SPATIAL_TOTAL_VALID_CROPS,
      SPATIAL_KEPT_CROPS,
      SPATIAL_KILLED_CROPS,
      SPATIAL_MISSED_CROPS,
      SPATIAL_SKIPPED_CROPS,
    ],
  },
  {
    key: "hardware",
    i18nKey: "utils.metrics.groups.hardware",
    internal: true,
    metrics: [
      SPATIAL_LIFTED,
      SPATIAL_ESTOPPED,
      SPATIAL_LASER_KEY,
      SPATIAL_INTERLOCK,
      SPATIAL_WATER_PROTECT,
    ],
  },
];
